import { jobDestination } from "@/components/job/jobNavigation";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { SupplierItems, SupplierMaterialSummary, OriginalInvoice } from "@/components/invoices/SupplierItems";
import api from "@/lib/api";
import { ChevronDown, DollarSign, TrendingUp, TrendingDown, Users, Truck, Boxes, Receipt, Wrench } from "lucide-react";

/**
 * Cost-per-job card.
 * Office/admin/super_admin only — caller is responsible for the role gate.
 * Fetches GET /api/jobs/{jobId}/cost_breakdown and renders:
 *   • Labour cost · Fleet cost · Total cost tiles
 *   • Quote / Profit-Loss / Margin %   (only if quoted_value set)
 *   • Tap-through breakdowns (per-worker for labour, per-asset for fleet)
 */
export function JobCostCard({ jobId, compact = false, base = `/app/jobs/${jobId}`, advanced = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLabour, setShowLabour] = useState(false);
  const [showFleet, setShowFleet] = useState(false);
  const [showGear, setShowGear] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api.get(`/jobs/${jobId}/cost_breakdown`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || "Failed to load costs"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]);

  if (loading) {
    return (
      <div className="mt-3 border-2 border-zinc-200 bg-zinc-50 p-4" data-testid={`job-cost-loading-${jobId}`}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Crunching the numbers…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="mt-3 border-2 border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid={`job-cost-error-${jobId}`}>{err}</div>
    );
  }
  if (!data) return null;

  const { labour, fleet, materials, invoices, totals, timesheet_count } = data;
  const gearData = data.gear || { rows: [], total_hours: 0, total_cost: 0 };
  const gearRows = gearData.rows || [];
  const matRows = materials?.rows || [];
  const matTotal = materials?.total_cost || 0;
  const invRows = invoices?.rows || [];
  const invTotal = invoices?.total_cost || 0;
  const quote = totals.quoted_value;
  const profit = totals.margin_amount;
  const marginPct = totals.margin_pct;
  const profitable = profit != null && profit >= 0;

  if (compact) return <section className="rounded-lg border border-border bg-card p-4" data-testid="job-position">
    <h2 className="font-semibold mb-3">Job position</h2>
    <dl className="grid sm:grid-cols-2 gap-3 text-sm"><div><dt className="text-muted-foreground">Recorded cost · live breakdown</dt><dd className="font-semibold text-lg">${totals.total_cost.toLocaleString("en-NZ")}</dd></div><div><dt className="text-muted-foreground">Quoted value</dt><dd className="font-semibold text-lg">{quote == null ? "Not set" : `$${quote.toLocaleString("en-NZ")}`}</dd></div></dl>
    <p className="text-sm text-muted-foreground mt-3">Recorded cost includes approved time, stock usage and linked supplier bills. Budget actuals also include manual costs and approved supplier-cost material entries.</p>
    <div className="flex flex-wrap gap-4 mt-3 text-sm font-medium text-orange-700 dark:text-orange-300"><Link to={jobDestination(jobId, "costs", { advanced })}>View Costs →</Link><Link to={jobDestination(jobId, "commercial", { advanced })}>View Commercial →</Link></div>
  </section>;

  return (
    <div className="hard-card overflow-hidden" data-testid={`job-cost-card-${jobId}`}>
      {/* Header strip */}
      <div className="bg-[#09090B] dark:bg-black/40 text-white px-3.5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="w-3.5 h-3.5 text-[#FF5F15]" />
          <span className="text-[10px] font-bold uppercase tracking-widest">Cost so far</span>
        </div>
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-400">
          {timesheet_count} approved ts
        </span>
      </div>

      {materials?.review_count > 0 && <p className="text-xs text-amber-700 mb-3">{materials.review_count} material record(s) need cost review. Totals exclude unknown costs and usage costs that may overlap supplier invoices.</p>}
      {/* Tile grid — 3 columns: Labour / Fleet / Gear · Materials / Invoices / Total */}
      <div className="grid grid-cols-3 gap-px bg-zinc-200 dark:bg-white/10">
        <CostTile
          label="Labour"
          icon={<Users className="w-3 h-3" />}
          amount={labour.total_cost}
          subline={`${labour.total_hours.toFixed(1)}h paid`}
          testid={`job-cost-labour-${jobId}`}
        />
        <CostTile
          label="Fleet"
          icon={<Truck className="w-3 h-3" />}
          amount={fleet.total_cost}
          subline={`${fleet.total_hours.toFixed(1)}h run`}
          testid={`job-cost-fleet-${jobId}`}
        />
        <CostTile
          label="Gear"
          icon={<Wrench className="w-3 h-3" />}
          amount={gearData.total_cost}
          subline={`${(gearData.total_hours || 0).toFixed(1)}h run`}
          testid={`job-cost-gear-${jobId}`}
        />
        <CostTile
          label="Materials"
          icon={<Boxes className="w-3 h-3" />}
          amount={matTotal}
          subline={matRows.length === 0 ? "none" : `${matRows.length} item${matRows.length === 1 ? "" : "s"}`}
          testid={`job-cost-materials-${jobId}`}
        />
        <CostTile
          label="Supplier invoices"
          icon={<Receipt className="w-3 h-3" />}
          amount={invTotal}
          subline={invRows.length === 0 ? "none" : `${invRows.length} inv${invRows.length === 1 ? "" : "s"}`}
          testid={`job-cost-invoices-${jobId}`}
        />
        <CostTile
          label="Total"
          accent
          amount={totals.total_cost}
          subline={profit == null ? "running" : "to date"}
          testid={`job-cost-total-${jobId}`}
        />
      </div>

      {/* Margin row — only when quoted */}
      {quote != null && (
        <div className={`px-3 py-3 grid grid-cols-3 gap-2 border-t-2 ${profitable ? "bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/20" : "bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/20"}`}
             data-testid={`job-margin-row-${jobId}`}>
          <MiniStat label="Quoted value" value={`$${quote.toLocaleString("en-NZ")}`} />
          <MiniStat
            label={profitable ? "Surplus so far" : "Shortfall so far"}
            value={`${profitable ? "+" : "−"}$${Math.abs(profit).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`}
            valueClass={profitable ? "text-green-700" : "text-red-700"}
          />
          <MiniStat
            label="Margin so far"
            value={marginPct == null ? "—" : `${marginPct.toFixed(1)}%`}
            icon={profitable
              ? <TrendingUp className="w-3 h-3 text-green-700 ml-1" />
              : <TrendingDown className="w-3 h-3 text-red-700 ml-1" />}
            valueClass={profitable ? "text-green-700" : "text-red-700"}
          />
        </div>
      )}
      {quote == null && (
        <div className="px-3 py-2 border-t border-zinc-200 dark:border-white/10 text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-center"
             data-testid={`job-margin-empty-${jobId}`}>
          Set a quoted value on this job to see margin %
        </div>
      )}

      <div className="px-4 py-3 text-sm text-muted-foreground space-y-2 border-t border-border">
        <p><strong>Materials:</strong> Recorded usage costs. Potential overlaps are held for review; confirmed supplier bills remain under Supplier invoices.</p>
        <p>Budget actuals also include manual costs and approved supplier-cost material entries. Supplier cost and customer charge are separate values.</p>
        {quote != null && <p>Margin so far compares quoted value with recorded costs to date; it is not forecast final margin.</p>}
      </div>
      {/* Breakdown toggles */}
      <div className="border-t border-zinc-200 dark:border-white/10 divide-y divide-zinc-200 dark:divide-white/10">
        <BreakdownSection
          label="Labour by worker"
          empty="No approved hours yet"
          open={showLabour}
          onToggle={() => setShowLabour((s) => !s)}
          rows={labour.rows}
          testid={`job-cost-labour-toggle-${jobId}`}
          renderRow={(r) => (
            <div key={r.user_id} className="flex items-center justify-between gap-3 py-1.5 text-xs"
                 data-testid={`job-cost-labour-row-${jobId}-${r.user_id}`}>
              <div className="min-w-0 flex-1 truncate">
                <span className="font-bold uppercase tracking-wider">{r.user_name}</span>
                <span className="text-zinc-500 font-mono ml-2">
                  {r.hours.toFixed(2)}h × ${r.hourly_rate.toFixed(2)}/hr
                </span>
              </div>
              <div className="font-mono font-bold">${r.cost.toFixed(2)}</div>
            </div>
          )}
        />
        <BreakdownSection
          label="Fleet by asset"
          empty={fleet.org_default_rate > 0
            ? "No fleet usage logged"
            : "No fleet usage logged · tip: set rates on each asset or a company default"}
          open={showFleet}
          onToggle={() => setShowFleet((s) => !s)}
          rows={fleet.rows}
          testid={`job-cost-fleet-toggle-${jobId}`}
          renderRow={(r) => (
            <div key={r.asset_id} className="flex items-center justify-between gap-3 py-1.5 text-xs"
                 data-testid={`job-cost-fleet-row-${jobId}-${r.asset_id}`}>
              <div className="min-w-0 flex-1 truncate">
                <span className="font-bold uppercase tracking-wider">{r.rego}</span>
                <span className="text-zinc-500 font-mono ml-2">
                  {r.hours.toFixed(2)}h × ${r.rate.toFixed(2)}/hr
                </span>
                {r.rate_source === "none" && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-widest text-red-600 font-bold">no rate set</span>
                )}
                {r.rate_source === "org_default" && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-widest text-zinc-400">default</span>
                )}
                {r.rate_source === "override" && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[#FF5F15] font-bold">override</span>
                )}
              </div>
              <div className="font-mono font-bold">${r.cost.toFixed(2)}</div>
            </div>
          )}
        />
        <BreakdownSection
          label="Gear & small equipment"
          empty="No gear usage logged · tip: set a charge-out rate on each gear item"
          open={showGear}
          onToggle={() => setShowGear((s) => !s)}
          rows={gearRows}
          testid={`job-cost-gear-toggle-${jobId}`}
          renderRow={(r) => (
            <div key={r.gear_id} className="flex items-center justify-between gap-3 py-1.5 text-xs"
                 data-testid={`job-cost-gear-row-${jobId}-${r.gear_id}`}>
              <div className="min-w-0 flex-1 truncate">
                <span className="font-bold uppercase tracking-wider">{r.name}</span>
                <span className="text-zinc-500 font-mono ml-2">
                  {r.hours.toFixed(2)}h × ${r.rate.toFixed(2)}/hr
                </span>
                {(!r.rate || r.rate === 0) && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-widest text-red-600 font-bold">no rate set</span>
                )}
              </div>
              <div className="font-mono font-bold">${r.cost.toFixed(2)}</div>
            </div>
          )}
        />
        <BreakdownSection
          label="Materials used"
          empty="No costed materials recorded against this job"
          open={showMaterials}
          onToggle={() => setShowMaterials((s) => !s)}
          rows={matRows}
          testid={`job-cost-materials-toggle-${jobId}`}
          renderRow={(r) => (
            <div key={r.id || r.material_id} className="flex items-center justify-between gap-3 py-1.5 text-xs"
                 data-testid={`job-cost-materials-row-${jobId}-${r.material_id}`}>
              <div className="min-w-0 flex-1 truncate">
                <span className="font-bold uppercase tracking-wider">{r.name}</span>
                <span className="text-zinc-500 font-mono ml-2">
                  {r.quantity} {r.unit}{r.cost_per_unit != null ? ` × $${r.cost_per_unit.toFixed(2)}` : ""}
                </span>
                {r.cost == null && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-widest text-red-600 font-bold">{r.cost_ownership === "supplier_invoice_review" ? "Supplier evidence · held for review" : "Historical / cost needs review"}</span>
                )}
              </div>
              <div className="text-right"><div className="font-mono font-bold">{r.cost == null ? "Needs review" : `$${r.cost.toFixed(2)} cost`}</div>{r.charge_value != null && <div className="text-zinc-500">${r.charge_value.toFixed(2)} charge</div>}{r.cost != null && r.charge_value != null && <div className="text-zinc-500">${(r.charge_value - r.cost).toFixed(2)} difference</div>}</div>
            </div>
          )}
        />
        <BreakdownSection
          label="Supplier invoices"
          empty="No supplier invoices linked to this job yet. Drop a PDF on the Invoices page to add one."
          open={showInvoices}
          onToggle={() => setShowInvoices((s) => !s)}
          rows={invRows}
          testid={`job-cost-invoices-toggle-${jobId}`}
          renderRow={(r) => (
            <details key={r.id} className="py-2 text-xs" data-testid={`job-cost-invoices-row-${jobId}-${r.id}`}>
              <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-2">
                <span><strong>{r.vendor}</strong>{r.invoice_number && <span className="text-zinc-500 ml-2">#{r.invoice_number}</span>}{r.invoice_date && <span className="text-zinc-400 ml-2">{r.invoice_date}</span>}</span>
                <span className="font-mono font-bold">${(r.total || 0).toFixed(2)} supplier cost</span>
              </summary>
              <div className="pt-2 space-y-2"><OriginalInvoice path={r.file_path} /><SupplierItems items={r.line_items} currency={r.currency} matches={r.fleet_matches} reviewed={r.operational_reviewed} /></div>
            </details>
          )}
        />
      </div>
      {invoices?.material_summary?.length > 0 && <div className="px-3 pb-3"><SupplierMaterialSummary rows={invoices.material_summary} /></div>}
    </div>
  );
}

function CostTile({ label, icon, amount, subline, accent, testid }) {
  return (
    <div className={`p-3 ${accent ? "bg-[#FF5F15] text-white" : "bg-white dark:bg-[#101620]"}`} data-testid={testid}>
      <div className={`flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest ${accent ? "text-white/80" : "text-zinc-500 dark:text-zinc-400"}`}>
        {icon} {label}
      </div>
      <div className="font-display font-black text-lg leading-none mt-1">
        ${(amount || 0).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}
      </div>
      <div className={`text-[9px] font-mono uppercase tracking-widest mt-1 ${accent ? "text-white/70" : "text-zinc-500 dark:text-zinc-500"}`}>
        {subline}
      </div>
    </div>
  );
}

function MiniStat({ label, value, valueClass = "", icon = null }) {
  return (
    <div className="text-center">
      <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-black text-base leading-none mt-1 inline-flex items-center justify-center ${valueClass}`}>
        {value}{icon}
      </div>
    </div>
  );
}

function BreakdownSection({ label, empty, open, onToggle, rows, renderRow, testid }) {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-zinc-50 dark:hover:bg-white/5"
        data-testid={testid}
      >
        <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-zinc-500">{rows?.length || 0}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-3 pb-2 divide-y divide-zinc-100">
          {hasRows ? rows.map(renderRow) : (
            <div className="py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{empty}</div>
          )}
        </div>
      )}
    </div>
  );
}
